import { getApiBaseUrl } from '../../config/apiBase';
import { ensureStreamGateToken, invalidateStreamGateCache } from '../../config/streamGate';
import { buildStreamUrl } from '../../hooks/useOsintStream';
import { STREAM_CHAT_ID } from '../../utils/investigationWorkspaceUtils';
import { buildPersonStreamParams } from '../../utils/personSearchResolve';
import { inferCompanyCountryFromDomain } from '../home/companyQueryParse';
import { partitionSseEvents } from './adminSearchBundle';

/**
 * Map an NLP Lab classification row to a live OSINT SSE launch plan.
 *
 * @param {object} row
 * @returns {Promise<{
 *   endpoint: string,
 *   params: Record<string, string>,
 *   moduleName: string,
 *   target: string,
 * }>}
 */
export async function resolveLaunchFromNlpRow(row) {
  const mode = String(row?.detected_mode || '').toLowerCase();
  const query = String(row?.query || '').trim();
  const chatId = STREAM_CHAT_ID || 'admin-nlp-lab';

  if (!query) {
    throw new Error('Empty query');
  }
  if (mode === 'invalid') {
    throw new Error(row?.guidance || 'Query is invalid — cannot run search');
  }

  if (mode === 'email') {
    const email = String(row?.email?.address || row?.email?.normalized || query).trim();
    return {
      endpoint: '/aggregate-email-stream',
      params: { email, chat_id: chatId },
      moduleName: 'email',
      target: email,
    };
  }

  if (mode === 'phone') {
    const phone = String(row?.phone?.e164 || query).trim();
    return {
      endpoint: '/aggregate-phone-stream',
      params: { phone, scope: 'ro', chat_id: chatId },
      moduleName: 'phone',
      target: phone,
    };
  }

  if (mode === 'company') {
    const domain = String(row?.domain || '').trim();
    const name = String(
      row?.stream_name || row?.company_name || row?.identity || query,
    ).trim();
    const params = {
      name: name || domain || query,
      chat_id: chatId,
      country: inferCompanyCountryFromDomain(domain) || 'RO',
    };
    if (domain) {
      params.domain = domain;
      params.primary_domain = domain;
    }
    const locs = Array.isArray(row?.location_tags)
      ? row.location_tags.map((t) => String(t || '').trim()).filter(Boolean)
      : [];
    if (locs.length) params.context_locations = locs.join(',');
    return {
      endpoint: '/aggregate-company-stream',
      params,
      moduleName: 'company',
      target: domain || name || query,
    };
  }

  // person (default)
  const built = await buildPersonStreamParams(query, {
    locationTags: Array.isArray(row?.location_tags) ? row.location_tags : [],
    contextTags: row?.company_name ? [String(row.company_name)] : [],
  });
  return {
    endpoint: '/aggregate-stream-name',
    params: { ...built.params, chat_id: chatId },
    moduleName: 'person',
    target: String(built.resolved?.name || row?.identity || query).trim() || query,
  };
}

/**
 * Run one OSINT SSE scan and collect results + logs until completion.
 *
 * @param {{
 *   baseUrl?: string,
 *   endpoint: string,
 *   params: Record<string, string>,
 *   signal?: AbortSignal,
 *   timeoutMs?: number,
 * }} opts
 * @returns {Promise<{
 *   results: object[],
 *   logs: object[],
 *   extractedLeads: object,
 *   completion: object|null,
 *   events: object[],
 * }>}
 */
export async function runOsintSearchCollect(opts) {
  const baseUrl = String(opts.baseUrl || getApiBaseUrl()).replace(/\/$/, '');
  const endpoint = opts.endpoint;
  const timeoutMs = Number(opts.timeoutMs) > 0 ? Number(opts.timeoutMs) : 12 * 60 * 1000;
  const externalSignal = opts.signal;

  if (!endpoint) throw new Error('Missing stream endpoint');

  let streamParams = { ...(opts.params || {}) };
  const gate = await ensureStreamGateToken(baseUrl);
  if (gate) streamParams = { ...streamParams, stream_gate: gate };

  const url = buildStreamUrl(baseUrl, endpoint, streamParams);

  return new Promise((resolve, reject) => {
    const events = [];
    let settled = false;
    let sessionId = null;
    /** @type {EventSource|null} */
    let es = null;
    let timer = null;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      if (timer) window.clearTimeout(timer);
      try {
        es?.close();
      } catch {
        /* ignore */
      }
      if (externalSignal) {
        externalSignal.removeEventListener('abort', onAbort);
      }
      fn(value);
    };

    const onAbort = () => {
      if (sessionId) {
        fetch(`${baseUrl}/cancel-stream-name`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        }).catch(() => {});
      }
      finish(reject, new Error('Search aborted'));
    };

    try {
      es = new EventSource(url);
    } catch (err) {
      finish(reject, err instanceof Error ? err : new Error(String(err)));
      return;
    }

    timer = window.setTimeout(() => {
      finish(reject, new Error(`Search timed out after ${Math.round(timeoutMs / 1000)}s`));
    }, timeoutMs);

    if (externalSignal) {
      if (externalSignal.aborted) {
        onAbort();
        return;
      }
      externalSignal.addEventListener('abort', onAbort);
    }

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data?.session_id && Object.keys(data).length <= 2) {
          sessionId = data.session_id;
          events.push(data);
          return;
        }
        events.push(data);

        const isTerminalDone = data?.done === true;
        const isOfficialEnd =
          isTerminalDone ||
          (data?.status === 'completed' &&
            !data?.ui_bucket &&
            !data?.source &&
            data?.type !== 'log') ||
          data?.status === 'cancelled';

        if (isOfficialEnd) {
          const partitioned = partitionSseEvents(events);
          finish(resolve, { ...partitioned, events });
        }
      } catch (err) {
        // Keep stream alive on parse errors; mirror home SSE resilience.
        if (typeof console !== 'undefined') {
          console.error('[admin-nlp] SSE parse error', err);
        }
      }
    };

    es.onerror = () => {
      invalidateStreamGateCache();
      if (events.length > 0) {
        const partitioned = partitionSseEvents(events);
        finish(resolve, { ...partitioned, events });
        return;
      }
      finish(reject, new Error('SSE connection error'));
    };
  });
}

/**
 * Resolve + run a full OSINT search for one NLP Lab row.
 *
 * @param {object} row
 * @param {{ baseUrl?: string, signal?: AbortSignal }} [opts]
 */
export async function runNlpRowSearch(row, opts = {}) {
  const launch = await resolveLaunchFromNlpRow(row);
  const collected = await runOsintSearchCollect({
    baseUrl: opts.baseUrl,
    endpoint: launch.endpoint,
    params: launch.params,
    signal: opts.signal,
  });
  return {
    ...launch,
    ...collected,
  };
}
